import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import { useAppContext } from '../../contexts/Storage';
import { useSingleLedger, useLedgers } from '../../components/Accounts';
import { LedgerTransactionRow } from '../../components/Accounts/use-single-ledger';
import LedgerPrintable from '../../components/Accounts/LedgerPrintable';
import { Button } from '../../components/ui/Button';
import { DatePicker } from '../../components/utils';
import { exportToExcel, ExcelColumnConfig } from '../../utils/excelExport';
import downloadIcon from '../../assets/images/download.png';
import printIcon from '../../assets/images/share.png';
import { AccountListItem } from '../../models/AccountListItem';
import { useCurrency } from '../../utils/globalCurrency';
import { formatDbDateTimeDisplay } from '../../utils/dateTime';



const formatDate = (dateStr: string) => {
  const formatted = formatDbDateTimeDisplay(dateStr).date;
  return formatted === 'Invalid Date' || formatted === 'N/A' ? dateStr : formatted;
};

const formatQuantity = (quantity: number) => {
  return quantity.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

interface LedgerExcelRow {
  side: string;
  date: string;
  action: string;
  particular: string;
  quantity: number;
  amount: number;
}

const SingleLedgerPage: React.FC = () => {
  const { entityId } = useParams<{ entityId: string }>();
  const { selectedRecord } = useAppContext();
  const { format } = useCurrency();
  const printableRef = useRef<HTMLDivElement>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);

  const parsedEntityId = entityId ? parseInt(entityId, 10) : undefined;

  const {
    mappedData,
    isLoading,
    handleDateFilter,
    filters,
  } = useSingleLedger({ entityId: parsedEntityId });

    // Fetch ledgers to get the entity name
  const { ledgers, isLoading: ledgersLoading } = useLedgers({});
  
    // Get the ledger entity name
  const ledgerEntityName = useMemo(() => {
    if (!ledgers || !parsedEntityId) return 'Ledger';
    
    const ledger = ledgers.find((l: AccountListItem) => l.id === parsedEntityId);
    const entityName = ledger?.transaction_entity_name || 'Ledger';
    
    // Append 'Ledger' if not already in the name
    if (entityName.toLowerCase().includes('ledger')) {
      return entityName;
    }
    return `${entityName} Ledger`;
  }, [ledgers, parsedEntityId]);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (startDate || endDate) {
      handleDateFilter(startDate || undefined, endDate || undefined);
    }
  }, [startDate, endDate, handleDateFilter]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    };
    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const captureAsImage = async (): Promise<string | null> => {
    if (!printableRef.current) return null;
    const wrapper = printableRef.current.parentElement;
    if (wrapper) {
      wrapper.style.overflow = 'visible';
      wrapper.style.height = 'auto';
      wrapper.style.position = 'fixed';
      wrapper.style.left = '0';
      wrapper.style.top = '0';
      wrapper.style.zIndex = '-9999';
      wrapper.style.opacity = '1';
    }
    try {
      return await toPng(printableRef.current, {
        backgroundColor: '#ffffff',
        pixelRatio: 2,
      });
    } catch {
      return null;
    } finally {
      if (wrapper) {
        wrapper.style.overflow = 'hidden';
        wrapper.style.height = '0';
        wrapper.style.position = '';
        wrapper.style.left = '';
        wrapper.style.top = '';
        wrapper.style.zIndex = '';
        wrapper.style.opacity = '';
      }
    }
  };

  const handleExportImage = useCallback(async () => {
    setShowExportMenu(false);
    const dataUrl = await captureAsImage();
    if (!dataUrl) {
      toast.error('Failed to generate image');
      return;
    }
    const link = document.createElement('a');
    link.download = 'ledger.png';
    link.href = dataUrl;
    link.click();
    toast.success('Image downloaded');
  }, []);

  const handleExportPDF = useCallback(async () => {
    setShowExportMenu(false);
    const dataUrl = await captureAsImage();
    if (!dataUrl) {
      toast.error('Failed to generate PDF');
      return;
    }
    const img = new Image();
    img.src = dataUrl;
    await new Promise<void>((resolve) => {
      img.onload = () => resolve();
    });
    const pixelRatio = 2;
    const pxToMm = 0.264583;
    const imgWidthMm = (img.width / pixelRatio) * pxToMm;
    const imgHeightMm = (img.height / pixelRatio) * pxToMm;
    const isLandscape = imgWidthMm > imgHeightMm;
    const pdf = new jsPDF({
      orientation: isLandscape ? 'landscape' : 'portrait',
      unit: 'mm',
      format: [imgWidthMm + 20, imgHeightMm + 20],
    });
    pdf.addImage(dataUrl, 'PNG', 10, 10, imgWidthMm, imgHeightMm);
    pdf.save('ledger.pdf');
    toast.success('PDF downloaded');
  }, []);

  const handleExportExcel = useCallback(() => {
    setShowExportMenu(false);
    if (!mappedData) {
      toast.error('No data to export');
      return;
    }

    const rows: LedgerExcelRow[] = [];

    // Add debit side transactions
    if (mappedData.debitSide.openingBalance > 0) {
      rows.push({
        side: 'DR',
        date: '',
        action: '',
        particular: 'Bal b/d',
        quantity: 0,
        amount: mappedData.debitSide.openingBalance,
      });
    }

    mappedData.debitSide.items.forEach((item) => {
      rows.push({
        side: 'DR',
        date: formatDate(item.date),
        action: item.action,
        particular: item.object + (item.indirectObject ? ` → ${item.indirectObject}` : ''),
        quantity: item.quantity,
        amount: item.amount,
      });
    });

    if (mappedData.debitSide.hasBalanceCarriedDown && mappedData.debitSide.closingBalance > 0) {
      rows.push({
        side: 'DR',
        date: '',
        action: '',
        particular: 'Bal c/d',
        quantity: 0,
        amount: mappedData.debitSide.closingBalance,
      });
    }

    // Add credit side transactions
    if (mappedData.creditSide.openingBalance > 0) {
      rows.push({
        side: 'CR',
        date: '',
        action: '',
        particular: 'Bal b/d',
        quantity: 0,
        amount: mappedData.creditSide.openingBalance,
      });
    }

    mappedData.creditSide.items.forEach((item) => {
      rows.push({
        side: 'CR',
        date: formatDate(item.date),
        action: item.action,
        particular: item.object + (item.indirectObject ? ` → ${item.indirectObject}` : ''),
        quantity: item.quantity,
        amount: item.amount,
      });
    });

    if (mappedData.creditSide.hasBalanceCarriedDown && mappedData.creditSide.closingBalance > 0) {
      rows.push({
        side: 'CR',
        date: '',
        action: '',
        particular: 'Bal c/d',
        quantity: 0,
        amount: mappedData.creditSide.closingBalance,
      });
    }

    const columns: ExcelColumnConfig<LedgerExcelRow>[] = [
      { key: 'side', header: 'Side' },
      { key: 'date', header: 'Date' },
      { key: 'action', header: 'Action' },
      { key: 'particular', header: 'Particular' },
      { key: 'quantity', header: 'Quantity', transform: (v: number) => formatQuantity(v) },
      { key: 'amount', header: 'Amount', transform: (v: number) => format(v) },
    ];

    exportToExcel({
      data: rows,
      columns,
      fileName: 'ledger',
      sheetName: 'Ledger',
      includeTimestamp: true,
    });
    toast.success('Excel downloaded');
  }, [mappedData]);

  const handlePrint = useCallback(() => {
    if (!printableRef.current) {
      toast.error('Nothing to print');
      return;
    }
    const htmlContent = printableRef.current.outerHTML;
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-9999px';
    iframe.style.top = '-9999px';
    iframe.style.width = '0';
    iframe.style.height = '0';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
      toast.error('Failed to prepare print content');
      document.body.removeChild(iframe);
      return;
    }
    doc.open();
    doc.write(`
      <html>
        <head>
          <title>Ledger</title>
          <style>
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          </style>
        </head>
        <body style="margin:0;padding:0;">
          ${htmlContent}
        </body>
      </html>
    `);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 100);
  }, []);

  if (!selectedRecord) {
    return (
      <div className="h-shink-from-navbar h-fill-from-navbar px-2 bg-bg_grey_light text-dark flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-grey_dark">Please select a record to view the ledger</p>
          <Link
            to="/records"
            className="text-primary hover:text-primary_dark underline mt-2 inline-block"
          >
            Go to Records
          </Link>
        </div>
      </div>
    );
  }

  if (!parsedEntityId || isNaN(parsedEntityId)) {
    return (
      <div className="h-shink-from-navbar h-fill-from-navbar px-2 bg-bg_grey_light text-dark flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-grey_dark">Invalid ledger account</p>
          <Link
            to="/records/accounts/ledgers"
            className="text-primary hover:text-primary_dark underline mt-2 inline-block"
          >
            Back to Ledgers
          </Link>
        </div>
      </div>
    );
  }

  const totalEntries =
    (mappedData?.debitSide.items.length || 0) +
    (mappedData?.creditSide.items.length || 0);

  const renderTransactionRow = (item: LedgerTransactionRow) => (
    <div
      key={item.id}
      className="grid grid-cols-12 gap-1 py-1.5 border-b border-grey_light/30 text-xs"
    >
      <div className="col-span-3 truncate">{formatDate(item.date)}</div>
      <div className="col-span-3 truncate">{item.action}</div>
      <div className="col-span-3 truncate">
        {item.object}
        {item.indirectObject ? ` → ${item.indirectObject}` : ''}
      </div>
      <div className="col-span-1 text-center">{formatQuantity(item.quantity)}</div>
      <div className="col-span-2 text-right">
                <span className="transaction-figure" title={format(item.amount)}>
          {format(item.amount)}
        </span>
      </div>
    </div>
  );

  const renderSide = (isDebitSide: boolean) => {
    const sideData = isDebitSide ? mappedData?.debitSide : mappedData?.creditSide;
    const oppositeSideData = isDebitSide ? mappedData?.creditSide : mappedData?.debitSide;
    const sideTitle = isDebitSide ? 'DR (Debit)' : 'CR (Credit)';
    const hasClosingBalance = sideData?.hasBalanceCarriedDown && sideData.closingBalance > 0;
    // Bal b/d appears on opposite side of Bal c/d
    const hasBalanceBroughtDown = oppositeSideData?.hasBalanceCarriedDown && oppositeSideData.closingBalance > 0;

    return (
      <div className="flex-1 flex flex-col min-w-0">
        <div className={`${isDebitSide ? 'text-start' : 'text-end'} mb-2 text-dark text-sm font-medium`}>
          {sideTitle}
        </div>

        <div className="grid grid-cols-12 gap-1 mb-2 pb-1.5 border-b border-grey_light text-grey_dark text-xs uppercase">
          <div className="col-span-3">Date</div>
          <div className="col-span-3">Action</div>
          <div className="col-span-3">Particular</div>
          <div className="col-span-1 text-center">Qty</div>
          <div className="col-span-2 text-right">Amount</div>
        </div>

        <div className="flex-1">
          {!sideData || sideData.items.length === 0 ? (
            <div className="text-center py-4 text-grey_dark text-xs">
              No {isDebitSide ? 'debit' : 'credit'} transactions
            </div>
          ) : (
            <div>
              {/* Transaction rows */}
              {sideData.items.map(renderTransactionRow)}

                            {/* Balance Carried Down (B/C) row */}
              {hasClosingBalance && (
                <div className="grid grid-cols-12 gap-1 py-1.5 text-xs font-medium text-grey_dark">
                  <div className="col-span-3"></div>
                  <div className="col-span-3"></div>
                  <div className="col-span-3 italic">Bal c/d</div>
                  <div className="col-span-1 text-center"></div>
                  <div className="col-span-2 text-right">
                                        <span className="transaction-figure" title={format(sideData.closingBalance)}>
                      {format(sideData.closingBalance)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="py-2 border-y border-grey_light mt-3">
          {/* Show total including balance carried down */}
          <div className="grid grid-cols-12 gap-1">
            <div className="col-span-10 text-right text-xs font-medium">
              Total:
            </div>
            <div className="col-span-2 text-right text-xs font-medium">
                            <span className="transaction-figure" title={format((sideData?.total || 0) + (sideData?.closingBalance || 0))}>
                {format((sideData?.total || 0) + (sideData?.closingBalance || 0))}
              </span>
            </div>
          </div>
                </div>
                {/* Balance Brought Down (B/B) row OR empty placeholder to maintain alignment */}
        <div className="grid grid-cols-12 gap-1 py-1.5 border-b border-grey_light/30 text-xs font-medium text-grey_dark min-h-[24px]">
          <div className="col-span-3">&nbsp;</div>
          <div className="col-span-3">&nbsp;</div>
          <div className="col-span-3 italic">
            {hasBalanceBroughtDown ? 'Bal b/d' : <>&nbsp;</>}
          </div>
          <div className="col-span-1 text-center">&nbsp;</div>
          <div className="col-span-2 text-right">
            {hasBalanceBroughtDown ? (
                            <span className="transaction-figure" title={format(oppositeSideData!.closingBalance)}>
                {format(oppositeSideData!.closingBalance)}
              </span>
            ) : (
              <>&nbsp;</>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="h-shink-from-navbar h-fill-from-navbar px-2 bg-bg_grey_light text-dark overflow-y-scroll">
        <div className="max-w-7xl mx-auto">
          <div className="p-6 bg-light rounded-lg flex justify-center items-center">
            <div className="text-grey_dark">Loading ledger...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-shink-from-navbar h-fill-from-navbar px-2 bg-bg_grey_light text-dark overflow-y-scroll">
      <div className="max-w-7xl mx-auto">
        <div className="p-6 pb-3 bg-light rounded-t-lg flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2">
                    <div className="w-full sm:w-auto mt-2">
            <h3 className="text-xl">{ledgerEntityName}</h3>
            <p className="text-grey_dark text-sm mt-1">
              Debit and credit transactions for this entity
            </p>
          </div>
          <div className="flex gap-2 mt-4 sm:mt-0 text-sm">
            <div className="relative" ref={exportDropdownRef}>
              <Button
                onClick={() => setShowExportMenu(!showExportMenu)}
                variant="google"
                className="flex items-center text-grey_dark"
              >
                <img src={downloadIcon} alt="Export" className="object-contain w-6 h-6 mr-2" />
                Export
              </Button>
              {showExportMenu && (
                <div className="absolute right-0 top-10 bg-light border border-grey_light rounded-lg shadow-lg z-50 min-w-[160px]">
                  <button
                    onClick={handleExportImage}
                    className="w-full text-left px-4 py-2.5 text-sm text-dark hover:bg-bg_grey_light transition-colors rounded-t-lg flex items-center gap-2"
                  >
                    <svg className="w-4 h-4 text-grey_dark" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    Image (PNG)
                  </button>
                  <button
                    onClick={handleExportPDF}
                    className="w-full text-left px-4 py-2.5 text-sm text-dark hover:bg-bg_grey_light transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4 text-grey_dark" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                    PDF
                  </button>
                  <button
                    onClick={handleExportExcel}
                    className="w-full text-left px-4 py-2.5 text-sm text-dark hover:bg-bg_grey_light transition-colors rounded-b-lg flex items-center gap-2"
                  >
                    <svg className="w-4 h-4 text-grey_dark" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                    Excel
                  </button>
                </div>
              )}
            </div>
            <Button
              onClick={handlePrint}
              variant="google"
              className="flex items-center text-grey_dark"
            >
              <img src={printIcon} alt="Print" className="object-contain w-6 h-6 mr-2" />
              Print
            </Button>
          </div>
        </div>

        <div className="bg-light rounded-lg p-6 mb-2">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex justify-between">
              <div className="mr-2">
                <DatePicker
                  id="start-date"
                  value={startDate}
                  onChange={setStartDate}
                  label="Start Date"
                  type="date"
                  className="mr-3"
                />
              </div>
              <div className="ml-2">
                <DatePicker
                  id="end-date"
                  value={endDate}
                  onChange={setEndDate}
                  label="End Date"
                  type="date"
                />
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm text-grey_dark">
              {mappedData && (
                <span className="px-3 py-3 rounded-md bg-bg_grey_light">
                  Balance: {format(mappedData.balanceAmount)} ({mappedData.balanceType})
                </span>
              )}
              <span>{totalEntries} entries</span>
            </div>
          </div>
        </div>

        <div className="bg-light rounded-lg p-4 sm:p-6 mb-4">
          <div className="flex flex-col lg:flex-row lg:items-stretch gap-6">
            {renderSide(true)}
            <div className="hidden lg:block w-px bg-grey_light"></div>
            {renderSide(false)}
          </div>
        </div>

        <div style={{ overflow: 'hidden', height: 0 }}>
                    <LedgerPrintable
            ref={printableRef}
            title={ledgerEntityName}
            data={mappedData}
            startDate={filters.start_date?.split(' ')[0]}
            endDate={filters.end_date?.split(' ')[0]}
          />
        </div>
      </div>
    </div>
  );
};

export default SingleLedgerPage;
